import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, ShieldCheck, Clock, MapPin, Users } from 'lucide-react';
import { Card, Button } from '../components';
import StatusDot from '../components/StatusDot';
import * as agenda from '../services/agendaService';
import type { BeschikbareStart, LocatieType } from '../services/agendaService';

const LOCATIE_TYPES: LocatieType[] = ['ONLINE', 'WERKGEVER', 'EXTERN'];

function dagKop(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

function tijd(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function duurLabel(minuten: number): string {
  const uren = Math.floor(minuten / 60);
  const rest = minuten % 60;
  if (uren && rest) return `${uren} u ${rest} min`;
  if (uren) return `${uren} uur`;
  return `${rest} min`;
}

/**
 * Anoniem medewerkerportaal: plannen via een aflopende link, zonder account.
 * De pagina toont geen tarieven, geen dossierinhoud en geen werkgeversgegevens.
 */
export const MedewerkerBoekingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('agenda');
  const queryClient = useQueryClient();

  const [gekozen, setGekozen] = useState<BeschikbareStart | null>(null);
  const [locatieType, setLocatieType] = useState<LocatieType>('ONLINE');
  const [bevestigd, setBevestigd] = useState<{ start_tijd: string; eind_tijd: string; status: 'PENDING_APPROVAL' | 'SCHEDULED' } | null>(null);
  const [geweigerd, setGeweigerd] = useState(false);

  const contextQuery = useQuery({
    queryKey: ['portaal-context', token],
    queryFn: () => agenda.getPortaalContext(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const slotsQuery = useQuery({
    queryKey: ['portaal-slots', token],
    queryFn: () => agenda.getPortaalSlots(token!),
    enabled: Boolean(token) && contextQuery.isSuccess,
    retry: false,
  });

  const boekMutation = useMutation({
    mutationFn: () =>
      agenda.boekPortaalAfspraak(token!, {
        start_tijd: gekozen!.start_tijd,
        locatie_type: locatieType,
      }),
    onSuccess: (data) => setBevestigd({ start_tijd: data.start_tijd, eind_tijd: data.eind_tijd, status: data.status }),
  });

  // Tegenvoorstel van de begeleider: akkoord plant in, weigeren geeft de agenda weer vrij om zelf te kiezen
  const antwoordMutation = useMutation({
    mutationFn: (akkoord: boolean) => agenda.beantwoordPortaalTegenvoorstel(token!, contextQuery.data!.open_afspraak!.id, akkoord),
    onSuccess: (data) => {
      const open = contextQuery.data!.open_afspraak!;
      if (data.status === 'SCHEDULED') {
        setBevestigd({ start_tijd: open.start_tijd, eind_tijd: open.eind_tijd, status: 'SCHEDULED' });
      } else {
        setGeweigerd(true);
        queryClient.invalidateQueries({ queryKey: ['portaal-context', token] });
        queryClient.invalidateQueries({ queryKey: ['portaal-slots', token] });
      }
    },
  });

  // Harde pool-grendel: zit de agenda van de gepinde professional vol, dan
  // blijft de pool open — toon de overige beschikbare experts uit de pool.
  const geenSlots = slotsQuery.isError || (slotsQuery.isSuccess && (slotsQuery.data?.slots?.length ?? 0) === 0);
  const alternatievenQuery = useQuery({
    queryKey: ['portaal-alternatieven', token],
    queryFn: () => agenda.getPoolAlternatieven(token!),
    enabled: Boolean(token) && geenSlots,
    retry: false,
  });

  const wisselMutation = useMutation({
    mutationFn: (professionalId: string) => agenda.kiesPoolProfessional(token!, professionalId),
    onSuccess: () => {
      setGekozen(null);
      queryClient.invalidateQueries({ queryKey: ['portaal-context', token] });
      queryClient.invalidateQueries({ queryKey: ['portaal-slots', token] });
      queryClient.invalidateQueries({ queryKey: ['portaal-alternatieven', token] });
    },
  });

  /** Slots gegroepeerd per dag, zodat de medewerker per dag kiest. */
  const perDag = useMemo(() => {
    const groepen = new Map<string, BeschikbareStart[]>();
    for (const slot of slotsQuery.data?.slots ?? []) {
      const dag = new Date(slot.start_tijd).toDateString();
      if (!groepen.has(dag)) groepen.set(dag, []);
      groepen.get(dag)!.push(slot);
    }
    return Array.from(groepen.entries());
  }, [slotsQuery.data]);

  if (contextQuery.isLoading) {
    return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-gray-500">…</div>;
  }

  if (contextQuery.isError) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="p-8 text-center">
          <p className="text-gray-900 font-medium">
            {t('portaal.ongeldig', { defaultValue: 'Deze planlink is verlopen of ongeldig.' })}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            {t('portaal.ongeldigHulp', { defaultValue: 'Vraag je begeleider om een nieuwe link.' })}
          </p>
        </Card>
      </div>
    );
  }

  const context = contextQuery.data!;

  if (bevestigd) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="p-8 text-center">
          <CalendarCheck className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-900">
            {bevestigd.status === 'PENDING_APPROVAL'
              ? t('portaal.verzoekVerstuurd', { defaultValue: 'Je afspraakverzoek is verstuurd' })
              : t('portaal.bevestigd', { defaultValue: 'Je afspraak staat ingepland' })}
          </h1>
          <p className="text-gray-700 mt-2">
            {dagKop(bevestigd.start_tijd)} · {tijd(bevestigd.start_tijd)}–{tijd(bevestigd.eind_tijd)}
          </p>
          {bevestigd.status === 'PENDING_APPROVAL' && (
            <p className="text-sm text-gray-600 mt-2">{t('portaal.verzoekUitleg')}</p>
          )}
          <p className="text-sm text-gray-600 mt-4">
            {t('portaal.bevestigdUitleg', {
              defaultValue: 'Je werkgever ziet alleen dát er een afspraak staat — nooit met wie of waarover.',
            })}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('portaal.titel', { defaultValue: 'Plan je gesprek' })}
        </h1>
        <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-green-600" />
          {t('portaal.anoniem', { defaultValue: 'Volledig anoniem — je werkgever ziet je keuze niet.' })}
        </p>
      </div>

      {geweigerd && !context.open_afspraak && <p className="text-sm text-gray-700">{t('portaal.geweigerd')}</p>}

      {context.open_afspraak?.status === 'PENDING_APPROVAL' && (
        <Card className="p-6 border-amber-200 bg-amber-50">
          <h2 className="font-semibold text-gray-900">{t('portaal.openVerzoekTitel')}</h2>
          <p className="text-gray-800 mt-1 capitalize">
            {dagKop(context.open_afspraak.start_tijd)} · {tijd(context.open_afspraak.start_tijd)}–{tijd(context.open_afspraak.eind_tijd)}
          </p>
          <p className="text-sm text-gray-600 mt-2">{t('portaal.openVerzoekTekst')}</p>
        </Card>
      )}

      {context.open_afspraak?.status === 'TEGENVOORSTEL' && (
        <Card className="p-6 border-blue-200 bg-blue-50">
          <h2 className="font-semibold text-gray-900">{t('portaal.tegenvoorstelTitel')}</h2>
          <p className="text-gray-900 mt-1 font-medium capitalize">
            {dagKop(context.open_afspraak.start_tijd)} · {tijd(context.open_afspraak.start_tijd)}–{tijd(context.open_afspraak.eind_tijd)}
          </p>
          <p className="text-sm text-gray-700 flex items-center gap-1 mt-0.5">
            <MapPin className="w-4 h-4" />
            {t(`locatie.${context.open_afspraak.locatie_type}`, { defaultValue: context.open_afspraak.locatie_type })}
            {context.open_afspraak.locatie_omschrijving ? ` — ${context.open_afspraak.locatie_omschrijving}` : ''}
          </p>
          {context.open_afspraak.aangevraagd_start_tijd && (
            <p className="text-xs text-gray-500 mt-1">
              {t('portaal.tegenvoorstelOorspronkelijk', {
                tijd: `${dagKop(context.open_afspraak.aangevraagd_start_tijd)} ${tijd(context.open_afspraak.aangevraagd_start_tijd)}`,
              })}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => antwoordMutation.mutate(true)} disabled={antwoordMutation.isPending}>
              {t('portaal.accepteren')}
            </Button>
            <Button variant="outline" onClick={() => antwoordMutation.mutate(false)} disabled={antwoordMutation.isPending}>
              {t('portaal.weigeren')}
            </Button>
          </div>
          {antwoordMutation.isError && <p className="text-sm text-red-600 mt-2">{t('portaal.antwoordMislukt')}</p>}
        </Card>
      )}

      <Card className="p-6">
        <div className="flex flex-wrap gap-6 text-sm">
          {context.professional_naam && (
            <div>
              <p className="text-gray-500">{t('portaal.begeleider', { defaultValue: 'Begeleider' })}</p>
              <p className="font-medium text-gray-900">{context.professional_naam}</p>
            </div>
          )}
          {/* Met een open verzoek beschrijft de context het vólgende gesprek: dan niet tonen */}
          {!context.open_afspraak && (
            <>
            <div>
              <p className="text-gray-500">{t('portaal.soort', { defaultValue: 'Soort gesprek' })}</p>
              <p className="font-medium text-gray-900">
                {t(`sessieType.${context.sessie_type}`, {
                  defaultValue: context.sessie_type === 'intake' ? 'Intake' : 'Vervolggesprek',
                })}
              </p>
            </div>
            <div>
              <p className="text-gray-500">{t('portaal.duur', { defaultValue: 'Duur' })}</p>
              <p className="font-medium text-gray-900 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {duurLabel(context.duur_minuten)}
              </p>
            </div>
            </>
          )}
        </div>
      </Card>

      {/* Met een openstaand verzoek of tegenvoorstel kan er niet opnieuw geboekt worden */}
      {!context.open_afspraak && (
        <>
          <Card className="p-6">
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {t('portaal.locatie', { defaultValue: 'Waar wil je afspreken?' })}
            </h2>
            {context.alleen_online && (
              <p className="text-sm text-gray-600 mb-2">
                {t('portaal.alleenOnline', { defaultValue: 'Deze begeleider werkt volledig online — het gesprek vindt plaats via videobellen.' })}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {(context.alleen_online ? (['ONLINE'] as LocatieType[]) : LOCATIE_TYPES).map((lt) => (
                <button
                  key={lt}
                  type="button"
                  onClick={() => setLocatieType(lt)}
                  className={`px-4 py-2 rounded-lg border text-sm transition ${
                    locatieType === lt
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {t(`locatie.${lt}`, { defaultValue: lt })}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              {t('portaal.kiesMoment', { defaultValue: 'Kies een moment' })}
            </h2>

            {slotsQuery.isError && (
              <p className="text-sm text-amber-700">
                {t('portaal.geenRuimte', { defaultValue: 'Er is op dit moment geen ruimte beschikbaar.' })}
              </p>
            )}

            {perDag.length === 0 && slotsQuery.isSuccess && (
              <p className="text-sm text-gray-500">
                {t('portaal.geenSlots', { defaultValue: 'Nog geen beschikbare tijden. Probeer het later opnieuw.' })}
              </p>
            )}

            {/* Harde pool-grendel: agenda vol → doorgeleiding naar andere experts uit de pool */}
            {geenSlots && alternatievenQuery.isSuccess && alternatievenQuery.data.alternatieven.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t('portaal.alternatievenTitel', { defaultValue: 'Andere beschikbare experts uit jouw pool' })}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {alternatievenQuery.data.wisselbaar
                    ? t('portaal.alternatievenUitleg', { defaultValue: 'De agenda van je begeleider zit vol. Kies gerust een andere expert — je plant dan direct bij die persoon.' })
                    : t('portaal.alternatievenNietWisselbaar', { defaultValue: 'De agenda van je begeleider zit vol. Omdat je traject al gestart is, kun je hier niet wisselen — neem contact op met je begeleider.' })}
                </p>
                {alternatievenQuery.data.wisselbaar && (
                  <div className="space-y-2">
                    {alternatievenQuery.data.alternatieven.map((alt) => (
                      <div key={alt.professional_id} className="flex items-center justify-between bg-white rounded-md border border-gray-200 px-3 py-2">
                        <span className="flex items-center gap-2 text-sm text-gray-900">
                          <StatusDot status={alt.pool_status} />
                          {alt.naam}
                        </span>
                        <Button
                          onClick={() => wisselMutation.mutate(alt.professional_id)}
                          disabled={wisselMutation.isPending}
                        >
                          {t('portaal.kiesExpert', { defaultValue: 'Kies deze expert' })}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {wisselMutation.isError && (
                  <p className="text-sm text-red-600 mt-2">
                    {(wisselMutation.error as any)?.response?.data?.message ??
                      t('portaal.wisselMislukt', { defaultValue: 'Wisselen is niet gelukt. Probeer het opnieuw.' })}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-5">
              {perDag.map(([dag, slots]) => (
                <div key={dag}>
                  <p className="text-sm font-medium text-gray-800 mb-2 capitalize">{dagKop(slots[0].start_tijd)}</p>
                  <div className="flex flex-wrap gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot.start_tijd}
                        type="button"
                        onClick={() => setGekozen(slot)}
                        className={`px-3 py-2 rounded-lg border text-sm transition ${
                          gekozen?.start_tijd === slot.start_tijd
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:border-emerald-400'
                        }`}
                      >
                        {tijd(slot.start_tijd)}–{tijd(slot.eind_tijd)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {boekMutation.isError && (
              <p className="text-sm text-red-600 mt-4">
                {(boekMutation.error as any)?.response?.data?.message ??
                  t('portaal.boekenMislukt', { defaultValue: 'Inplannen mislukt. Kies een ander moment.' })}
              </p>
            )}

            <div className="mt-6 flex justify-end">
              <Button onClick={() => boekMutation.mutate()} disabled={!gekozen || boekMutation.isPending}>
                {t('portaal.bevestig', { defaultValue: 'Bevestig boeking' })}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default MedewerkerBoekingPage;
